import {getAccountCreditsRouteLocation} from "@/domains/-account/-credits/routes.ts";
import {getClientListRouteLocation} from "@/domains/-coachee/-list/routes.ts";
import {loadWorkspaceOrder, saveWorkspaceOrder} from "@/domains/-session/functions/workspaceOrder.ts";
import {PersonMe} from "@/domains/-session/models/PersonMe";
import {WorkspacePersonMe} from "@/domains/-session/models/WorkspacePersonMe.ts";
import {Workspace} from "@/domains/-workspace/models/Workspace.ts";
import {getUserDefaultLang, setLocales} from "@/i18n";
import {isExactActiveRoute, router} from "@/router";
import {Lang, sdk, WorkspaceId} from "@/sdk";
import {AxiosError} from "axios";
import {reactive} from "vue";
import {Router} from "vue-router";

type ContractError = AxiosError<{
  detail: {
    key: 'legal.contract.unsigned';
    contract_name: string;
    contract_id: number;
    language: string;
  }
}> & {
  response: {
    data: {
      detail: {
        key: 'legal.contract.unsigned';
        contract_name: string;
        contract_id: number;
        language: string;
      }
    }
  }
};

type AuthenticationError = AxiosError<{
  detail: 'auth.unauthenticated';
}>

function isContractError(error: AxiosError): error is ContractError {
  return 'response' in error
    && typeof error.response === 'object'
    && error.response !== null
    && 'data' in error.response
    && typeof error.response.data === 'object'
    && error.response.data !== null
    && 'detail' in error.response.data
    && typeof error.response.data.detail === 'object'
    && error.response.data.detail !== null
    && 'key' in error.response.data.detail
    && error.response.data.detail.key === 'legal.contract.unsigned';
}

function isAuthenticationError(error: AxiosError): error is AuthenticationError {
  return 'response' in error
    && typeof error.response === 'object'
    && error.response !== null
    && 'data' in error.response
    && typeof error.response.data === 'object'
    && error.response.data !== null
    && 'detail' in error.response.data
    && error.response.data.detail === 'auth.unauthenticated';
}


async function handleContractError(error: ContractError): Promise<void> {
  if (error.response.data.detail.contract_name.match(/^hub_cgu/g)) {
    await router.push({
      name: `tos`, query: {
        contract_id: error.response.data.detail.contract_id,
        contract_name: error.response.data.detail.contract_name,
        language: error.response.data.detail.language,
      }
    })
  } else if (error.response.data.detail.contract_name.match(/^hub_legal/g)) {
    await router.push({
      name: `legal`, query: {
        contract_id: error.response.data.detail.contract_id,
        contract_name: error.response.data.detail.contract_name,
        language: error.response.data.detail.language,
      }
    })
  }
}

export class SessionStore {
  public loaded: boolean;
  public me: undefined | PersonMe;
  public users: WorkspacePersonMe[];
  public workspaces: Workspace[];
  public wsp: undefined | WorkspacePersonMe;
  private promise: undefined | Promise<void>

  constructor() {
    this.me = undefined;
    this.wsp = undefined;
    this.users = [];
    this.workspaces = [];
    this.loaded = false;
    this.promise = undefined;

  }

  get isAuthenticated(): boolean {
    return this.me !== undefined && this.loaded;
  }

  async loadUser(forceReload: boolean = false): Promise<void> {
    if (this.loaded && !forceReload) return;
    if (this.promise) return this.promise;

    this.promise = (async () => {
      try {
        const meResponse = await sdk.getMe();
        const person = new PersonMe(meResponse.data);
        this.me = person;
        setLocales(this.lang)

        const workspacesResponse = await sdk.getWorkspaces();
        const workspaces = workspacesResponse.data.map(data => new Workspace(data)).filter(workspace => {
          const roles = meResponse.data.roles.filter(role => role.workspace_id === workspace.id);
          // IMPORTANT NOTE : we only display workspaces where me have a role handled by the hub (admin, coach, extern)
          return roles.some(role => ["admin", "coach", "extern"].includes(role.person_role));
        });

        this.users = workspaces.map(workspace => {
          return new WorkspacePersonMe(workspace, person, meResponse.data.roles)
        })

        const workspaceOrder = loadWorkspaceOrder();
        if (workspaceOrder === null) {
          this.users.sort((wsp1, wsp2) => {
            const isExtern1 = wsp1.extern !== undefined
            const isExtern2 = wsp2.extern !== undefined
            if (isExtern1 !== isExtern2) return Number(isExtern1) - Number(isExtern2)
            return wsp1.workspace.name.localeCompare(wsp2.workspace.name)
          });

          this.workspaces = this.users.map(wsp => wsp.workspace);

          saveWorkspaceOrder(this.workspaces);
        } else {
          this.users.sort((wsp1, wsp2) => {
            const index1 = workspaceOrder.indexOf(wsp1.workspace.id)
            const index2 = workspaceOrder.indexOf(wsp2.workspace.id)
            if (index1 < index2) return -1;
            if (index1 > index2) return 1;
            return 0;
          });
          this.workspaces = this.users.map(wsp => wsp.workspace);
        }

        this.loaded = true;
      } catch (error) {
        this.me = undefined;
        this.users = [];
        this.workspaces = [];

        if (isContractError(error))
          return await handleContractError(error);

        if (isAuthenticationError(error)) {
          this.unload();
          return; // We don't redirect as this is supposed to happen only in `router.ts`.
        }

        this.unload();
        throw error;
      } finally {
        this.promise = undefined;
      }
    })();
    return this.promise;
  }

  setWorkspace(workspaceId: WorkspaceId, router: Router) {
    this.wsp = this.users.find(user => user.workspace.id === workspaceId);
    if (isExactActiveRoute('coachee_overview')) {
      router.push(getClientListRouteLocation(workspaceId)).then();
    } else if (isExactActiveRoute('coachee_journey')) {
      router.push(getClientListRouteLocation(workspaceId)).then();
    } else if (isExactActiveRoute('account_credits')) {
      router.push(getAccountCreditsRouteLocation(workspaceId)).then();
    } else {
      router.push({params: {workspaceId}}).then()
    }
  }

  invertWorkspaces(index1: number, index2: number) {
    const workspace1 = this.workspaces[index1];
    this.workspaces[index1] = this.workspaces[index2];
    this.workspaces[index2] = workspace1;

    const user1 = this.users[index1];
    this.users[index1] = this.users[index2];
    this.users[index2] = user1;
  }

  saveWorkspaceOrder() {
    saveWorkspaceOrder(this.workspaces);
  }

  async signOut() {
    await sdk.signOut();
    this.unload();
  }

  unload() {
    this.me = undefined;
    this.users = [];
    this.workspaces = [];
    this.loaded = false;
  }

  get lang(): Lang {
    return this.me?.lang ?? getUserDefaultLang();
  }
}


let sessionStore: SessionStore;

export function useSessionStore(): SessionStore {
  if (!sessionStore)
    sessionStore = reactive(new SessionStore()) as SessionStore;

  return sessionStore;
}